import { pinyin } from 'pinyin-pro';
import http from '../api/httpRequest.js';

let groupById = new Map();
const letterCache = new Map();

function computeGroupLetter(name) {
	const text = (name || '').trim();
	if (!text) {
		return '#';
	}
	if (letterCache.has(text)) {
		return letterCache.get(text);
	}
	const first = text[0];
	let letter = '#';
	if (/[A-Za-z]/.test(first)) {
		letter = first.toUpperCase();
	} else {
		try {
			const py = pinyin(first, { toneType: 'none', pattern: 'first' });
			const ch = (typeof py === 'string' ? py[0] : '') || '';
			letter = /[A-Za-z]/.test(ch) ? ch.toUpperCase() : '#';
		} catch (e) {
			letter = '#';
		}
	}
	letterCache.set(text, letter);
	return letter;
}

function setGroupMapEntry(group) {
	if (!group || group.id == null) {
		return;
	}
	groupById.set(group.id, group);
	groupById.set(String(group.id), group);
	groupById.set(Number(group.id), group);
}

function getGroupById(id) {
	if (id == null || id === '') {
		return;
	}
	return groupById.get(id) || groupById.get(Number(id)) || groupById.get(String(id));
}

function slimGroup(raw, prev) {
	const source = Object.assign({}, prev || {}, raw || {});
	delete source.members;
	delete source.memberList;
	delete source.users;
	const showGroupName = source.showGroupName || source.name || '';
	source.showGroupName = showGroupName;
	source.letter = (prev && prev.letter && prev.showGroupName === showGroupName)
		? prev.letter
		: computeGroupLetter(showGroupName);
	return source;
}

function resetGroupIndex() {
	groupById = new Map();
}

export default {

	state: {
		groups: [],
		joinRequests: [],
		myJoinRequests: []
	},
	mutations: {
		setGroups(state, groups) {
			resetGroupIndex();
			const source = Array.isArray(groups) ? groups : [];
			const list = new Array(source.length);
			let size = 0;
			for (let i = 0; i < source.length; i++) {
				const raw = source[i];
				if (!raw || raw.id == null) {
					continue;
				}
				const group = slimGroup(raw);
				setGroupMapEntry(group);
				list[size++] = group;
			}
			list.length = size;
			state.groups = list;
		},
		addGroup(state, group) {
			if (!group || group.id == null) {
				return;
			}
			const prev = getGroupById(group.id);
			if (prev) {
				this.commit("updateGroup", group)
			} else {
				const next = slimGroup(group);
				state.groups.unshift(next);
				setGroupMapEntry(next);
			}
		},
		removeGroup(state, id) {
			const prev = getGroupById(id);
			if (!prev) {
				return;
			}
			prev.quit = true;
		},
		updateGroup(state, group) {
			if (!group || group.id == null) {
				return;
			}
			const prev = getGroupById(group.id);
			if (!prev) {
				this.commit("addGroup", group);
				return;
			}
			const next = slimGroup(group, prev);
			Object.assign(prev, next);
			delete prev.members;
			delete prev.memberList;
			delete prev.users;
			setGroupMapEntry(prev);
		},
		updateTopMessage(state, [id, topMessage]) {
			let group = this.getters.findGroup(id);
			if (group) {
				group.topMessage = topMessage;
			}
		},
		setJoinRequests(state, requests) {
			state.joinRequests = Array.isArray(requests) ? requests : [];
		},
		setMyJoinRequests(state, requests) {
			state.myJoinRequests = Array.isArray(requests) ? requests : [];
		},
		addJoinRequest(state, request) {
			if (!request) {
				return;
			}
			if (!request.id) {
				state.joinRequests.unshift(request);
				return;
			}
			const idx = state.joinRequests.findIndex((item) => item.id == request.id);
			if (idx >= 0) {
				state.joinRequests.splice(idx, 1, {
					...state.joinRequests[idx],
					...request
				});
			} else {
				state.joinRequests.unshift(request);
			}
		},
		removeJoinRequest(state, request) {
			if (!request) {
				return;
			}
			if (request.id) {
				state.joinRequests = state.joinRequests.filter((item) => item.id != request.id);
				return;
			}
			if (request.groupId && request.sendId) {
				state.joinRequests = state.joinRequests.filter((item) => !(item.groupId == request.groupId && item.sendId == request.sendId));
			}
		},
		addMyJoinRequest(state, request) {
			if (!request) {
				return;
			}
			if (!request.id) {
				state.myJoinRequests.unshift(request);
				return;
			}
		},
		updateMyJoinRequestStatus(state, request) {
			if (!request) {
				return;
			}
			if (request.id) {
				const idx = state.myJoinRequests.findIndex((item) => item.id == request.id);
				if (idx >= 0) {
					state.myJoinRequests.splice(idx, 1, {
						...state.myJoinRequests[idx],
						...request,
						status: 3
					});
				}
				return;
			}
			if (request.groupId && request.sendId) {
				const idx = state.myJoinRequests.findIndex((item) => item.groupId == request.groupId && item.sendId == request.sendId);
				if (idx >= 0) {
					state.myJoinRequests.splice(idx, 1, {
						...state.myJoinRequests[idx],
						...request,
						status: 3
					});
				}
			}
		},
		clear(state) {
			resetGroupIndex();
			state.groups = [];
			state.joinRequests = [];
		}
	},
	actions: {
		loadGroup(context) {
			return new Promise((resolve, reject) => {
				http({
					url: '/group/list',
					method: 'GET'
				}).then((groups) => {
					context.commit("setGroups", groups);
					resolve();
				}).catch((res) => {
					reject(res);
				})
			});
		},
		loadGroupJoinRequests(context) {
			return new Promise((resolve, reject) => {
				http({
					url: '/group/join/pending',
					method: 'GET'
				}).then((requests) => {
					context.commit("setJoinRequests", requests);
					resolve(requests);
				}).catch((err) => {
					context.commit("setJoinRequests", []);
					reject(err);
				});
			});
		},
		loadMyJoinRequests(context) {
			return new Promise((resolve, reject) => {
				http({
					url: '/group/join/my',
					method: 'GET'
				}).then((requests) => {
					context.commit("setMyJoinRequests", requests);
					resolve(requests);
				}).catch((err) => {
					context.commit("setMyJoinRequests", []);
					reject(err);
				});
			});
		}
	},
	getters: {
		findGroup: (state) => (id) => {
			return getGroupById(id);
		},
		isGroup: (state) => (id) => {
			const group = getGroupById(id);
			return !!(group && !group.quit);
		},

	}
}
